import type { FeedItem } from "@scrile/contracts";
import type { ApiErrorCode } from "../api/errors";

/**
 * Очередь исходящих: оптимистичная отправка. Сообщение появляется в переписке сразу
 * («sending»), после 202 — «sent», при сбое — повтор сам (сеть, сервер моргнул, 429 по
 * Retry-After) или по кнопке («failed»).
 *
 * Исходящее живёт в очереди, пока его зеркало не пришло лентой (from: user): тогда
 * пузырь рисуется из ленты, а местный убирается. Зеркало узнаём только по seq из 202.
 * Если ответ потерялся, повтор с тем же idempotency-key возвращает тот же seq.
 * Совпадение текста или вида голосового недостаточно: человек может повториться
 * либо отправить другое сообщение из соседней вкладки.
 *
 * Всё здесь — чистые функции над массивом: хук держит состояние, тесты — таблицы.
 */

export type OutgoingStatus = "sending" | "sent" | "failed";

export type OutgoingBody =
  | { kind: "text"; text: string }
  | {
      kind: "voice";
      /** Запись до загрузки; после — не нужна, но держим для повтора POST /messages. */
      blob: Blob | null;
      /** Местный адрес записи — слушать своё, пока лента не принесла подписанную ссылку. */
      localUrl: string | null;
      mime: string;
      durationMs: number;
      /** Ключ после загрузки: повтор после сбоя POST /messages не грузит файл заново. */
      voiceAssetKey: string | null;
    };

export interface OutgoingError {
  code: ApiErrorCode;
  /** Кнопка «Retry» имеет смысл: сеть, сервер, частота. Не имеет: validation, 402 до оплаты. */
  retryable: boolean;
}

export interface Outgoing {
  /** Местный id; он же ключ повтора (idempotency-key) для сервера. */
  id: string;
  body: OutgoingBody;
  status: OutgoingStatus;
  /** Часы клиента в момент отправки — время на пузыре до прихода зеркала. */
  createdAt: number;
  /** Курсор ленты в момент отправки: зеркало будет с seq больше. */
  afterSeq: number;
  seq: number | null;
  /** Сколько раз отправка уже не удалась подряд (для паузы и потолка авто-повтора). */
  attempts: number;
  error: OutgoingError | null;
  /** Когда повторить самим; null — не повторяем (ждём кнопку или уже ушло). */
  retryAt: number | null;
}

/** Авто-повторов подряд, дальше — кнопка. */
export const MAX_AUTO_RETRIES = 3;
const RETRY_BASE_MS = 1_500;
const RETRY_MAX_MS = 20_000;
/** 429 без Retry-After — подождать столько. */
const RATE_LIMIT_DEFAULT_MS = 5_000;

export function enqueue(
  queue: readonly Outgoing[],
  input: { id: string; body: OutgoingBody; now: number; afterSeq: number },
): Outgoing[] {
  const item: Outgoing = {
    id: input.id,
    body: input.body,
    status: "sending",
    createdAt: input.now,
    afterSeq: input.afterSeq,
    seq: null,
    attempts: 0,
    error: null,
    retryAt: null,
  };
  return [...queue, item];
}

function patch(queue: readonly Outgoing[], id: string, update: (item: Outgoing) => Outgoing): Outgoing[] {
  return queue.map((item) => (item.id === id ? update(item) : item));
}

export function markSent(queue: readonly Outgoing[], id: string, seq: number): Outgoing[] {
  return patch(queue, id, (item) => ({ ...item, status: "sent", seq, error: null, retryAt: null }));
}

/** Голосовое загружено: ключ запоминаем, чтобы повтор не грузил заново. */
export function markUploaded(queue: readonly Outgoing[], id: string, voiceAssetKey: string): Outgoing[] {
  return patch(queue, id, (item) =>
    item.body.kind === "voice" ? { ...item, body: { ...item.body, voiceAssetKey } } : item,
  );
}

export interface RetryDecision {
  retryable: boolean;
  /** Через сколько повторить самим; null — не самим. */
  autoDelayMs: number | null;
}

/**
 * Что делать после сбоя попытки номер attempts (с 1):
 * - сеть, недоступность, 5xx — повтор с растущей паузой, до MAX_AUTO_RETRIES, потом кнопка;
 * - 429 — повтор по Retry-After (сервер сам сказал когда), тоже до потолка;
 * - 402 — ни сам, ни сразу: сначала подписка (кнопка появится, повтор разрешён);
 * - validation, forbidden, conflict и прочее — повтор того же не поможет.
 */
export function retryDecision(code: ApiErrorCode, attempts: number, retryAfterSec: number | null = null): RetryDecision {
  switch (code) {
    case "network":
    case "unavailable":
    case "internal":
    case "bad_response": {
      const auto = attempts <= MAX_AUTO_RETRIES;
      const delay = Math.min(RETRY_MAX_MS, RETRY_BASE_MS * 2 ** Math.max(0, attempts - 1));
      return { retryable: true, autoDelayMs: auto ? delay : null };
    }
    case "rate_limited": {
      const auto = attempts <= MAX_AUTO_RETRIES;
      const delay = retryAfterSec !== null ? Math.min(60_000, Math.max(1_000, retryAfterSec * 1000)) : RATE_LIMIT_DEFAULT_MS;
      return { retryable: true, autoDelayMs: auto ? delay : null };
    }
    case "payment_required":
      return { retryable: true, autoDelayMs: null };
    case "unauthorized":
      return { retryable: true, autoDelayMs: null };
    default:
      return { retryable: false, autoDelayMs: null };
  }
}

export function markFailed(
  queue: readonly Outgoing[],
  id: string,
  input: { code: ApiErrorCode; now: number; retryAfterSec?: number | null },
): Outgoing[] {
  return patch(queue, id, (item) => {
    const attempts = item.attempts + 1;
    const decision = retryDecision(input.code, attempts, input.retryAfterSec ?? null);
    const auto = decision.autoDelayMs !== null;
    return {
      ...item,
      // Повторим сами — для человека сообщение всё ещё «отправляется»
      status: auto ? "sending" : "failed",
      attempts,
      error: { code: input.code, retryable: decision.retryable },
      retryAt: auto ? input.now + (decision.autoDelayMs ?? 0) : null,
    };
  });
}

/** Кнопка «Retry»: снова в отправку, счёт попыток — заново (человек сам решил). */
export function retryNow(queue: readonly Outgoing[], id: string): Outgoing[] {
  return patch(queue, id, (item) =>
    item.status === "failed" || item.retryAt !== null
      ? { ...item, status: "sending", attempts: 0, error: null, retryAt: null }
      : item,
  );
}

/** Удалить неотправленное (человек передумал). */
export function discard(queue: readonly Outgoing[], id: string): Outgoing[] {
  return queue.filter((item) => item.id !== id);
}

/** Чьё время повтора пришло. */
export function dueRetries(queue: readonly Outgoing[], now: number): string[] {
  return queue.filter((item) => item.status === "sending" && item.retryAt !== null && item.retryAt <= now).map((i) => i.id);
}

/** Ближайший срок авто-повтора — когда поставить таймер. */
export function nextRetryAt(queue: readonly Outgoing[]): number | null {
  let next: number | null = null;
  for (const item of queue) {
    if (item.status === "sending" && item.retryAt !== null) next = next === null ? item.retryAt : Math.min(next, item.retryAt);
  }
  return next;
}

/** Снять отметку «повторить в» перед самой попыткой: пока она идёт, второй таймер не нужен. */
export function markAttempting(queue: readonly Outgoing[], id: string): Outgoing[] {
  return patch(queue, id, (item) => ({ ...item, status: "sending", retryAt: null }));
}

/**
 * Убрать из очереди то, что уже пришло лентой. Возвращает новую очередь и seq зеркал,
 * которые «забрали» местные сообщения (им лента рисует отметку «sent»).
 */
export function reconcile(
  queue: readonly Outgoing[],
  feed: readonly FeedItem[],
): { queue: Outgoing[]; claimed: number[] } {
  if (queue.length === 0) return { queue: queue as Outgoing[], claimed: [] };
  const bySeq = new Map<number, FeedItem>();
  for (const item of feed) if (item.from === "user") bySeq.set(item.seq, item);
  const claimed: number[] = [];
  const left: Outgoing[] = [];
  for (const item of queue) {
    if (item.seq !== null && bySeq.has(item.seq)) claimed.push(item.seq);
    else left.push(item);
  }
  return { queue: left, claimed };
}

/** Тело POST /messages для исходящего; голосовое без ключа ещё надо загрузить. */
export function requestBody(item: Outgoing): { text: string } | { voiceAssetKey: string } | null {
  if (item.body.kind === "text") return { text: item.body.text.trim() };
  return item.body.voiceAssetKey ? { voiceAssetKey: item.body.voiceAssetKey } : null;
}
